import {
  createContext,
  useContext,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import {
  APP_CHROME_CANVAS,
  SURFACE_PAGE_DESCRIPTION,
  SURFACE_PAGE_KICKER,
  SURFACE_PAGE_META,
  SURFACE_PAGE_MOBILE_TOP_PAD,
  SURFACE_PAGE_SECTION_TITLE,
  SURFACE_PAGE_TITLE,
  SURFACE_PAGE_TITLE_LARGE,
  SURFACE_PAGE_WIDTH_CONTENT,
  SURFACE_PAGE_WIDTH_FULL,
  SURFACE_PAGE_WIDTH_NARROW,
  SURFACE_PAGE_CARD,
} from '@/shared/ui-class';
import { cn } from '@/shared/cn';
import { ScrollCue } from './ScrollCue';

export type SurfacePageWidth = 'content' | 'narrow' | 'full';

const WIDTH_CLASS: Record<SurfacePageWidth, string> = {
  content: SURFACE_PAGE_WIDTH_CONTENT,
  narrow: SURFACE_PAGE_WIDTH_NARROW,
  full: SURFACE_PAGE_WIDTH_FULL,
};

export function surfacePageWidthClassName(width: SurfacePageWidth): string {
  return WIDTH_CLASS[width];
}

export type SurfacePageHeaderProps = {
  title: string;
  description?: string;
  kicker?: string;
  actions?: ReactNode;
  tabs?: ReactNode;
  toolbar?: ReactNode;
  toolbarActions?: ReactNode;
  sectionTitle?: string;
  sectionSubtitle?: string;
  meta?: ReactNode;
  titleSize?: 'default' | 'large';
  className?: string;
};

/**
 * Unified page title stack. Owns the single top-level `<h1>` — sections below
 * must use `<h2>` via {@link SurfacePageSectionHeader} or local headings.
 */
export function SurfacePageHeader({
  title,
  description,
  kicker,
  actions,
  tabs,
  toolbar,
  toolbarActions,
  sectionTitle,
  sectionSubtitle,
  meta,
  titleSize = 'default',
  className,
}: SurfacePageHeaderProps) {
  const showToolbar = tabs || toolbar || toolbarActions;
  const showSection = sectionTitle || meta;

  return (
    <header className={cn('space-y-4', className)}>
      <div className="space-y-2">
        {kicker ? <p className={SURFACE_PAGE_KICKER}>{kicker}</p> : null}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <h1
            className={cn(
              titleSize === 'large'
                ? SURFACE_PAGE_TITLE_LARGE
                : SURFACE_PAGE_TITLE,
              'min-w-0',
            )}
          >
            {title}
          </h1>
          {actions ? (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          ) : null}
        </div>
        {description ? (
          <p className={cn(SURFACE_PAGE_DESCRIPTION, 'max-w-2xl')}>
            {description}
          </p>
        ) : null}
      </div>

      {showToolbar ? (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {tabs}
            {toolbar}
          </div>
          {toolbarActions ? (
            <div className="flex shrink-0 items-center gap-2">
              {toolbarActions}
            </div>
          ) : null}
        </div>
      ) : null}

      {showSection ? (
        <SurfacePageSectionHeader
          title={sectionTitle ?? ''}
          subtitle={sectionSubtitle}
          meta={meta}
        />
      ) : null}
    </header>
  );
}

/** Shared page title band — thin alias over {@link SurfacePageHeader}. */
export function PageHeader(props: SurfacePageHeaderProps) {
  return <SurfacePageHeader {...props} />;
}

export type SettingsPageHeaderProps = Pick<
  SurfacePageHeaderProps,
  'title' | 'description' | 'kicker' | 'actions'
>;

/** Settings routes — default `Settings` kicker. */
export function SettingsPageHeader({
  kicker = 'Settings',
  ...props
}: SettingsPageHeaderProps) {
  return <SurfacePageHeader kicker={kicker} {...props} />;
}

export type SurfacePageSectionHeaderProps = {
  title: string;
  /** Muted text after the title; a node when a state pill rides beside it (case-page Living FMEA). */
  subtitle?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  className?: string;
};

export function SurfacePageSectionHeader({
  title,
  subtitle,
  meta,
  actions,
  className,
}: SurfacePageSectionHeaderProps) {
  if (!title && !meta && !actions) return null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2',
        className,
      )}
    >
      {title ? (
        <h2 className={SURFACE_PAGE_SECTION_TITLE}>
          {title}
          {subtitle ? (
            <>
              <span className="font-normal text-muted"> · </span>
              {subtitle}
            </>
          ) : null}
        </h2>
      ) : (
        <span />
      )}
      {(meta || actions) && (
        <div className="flex flex-wrap items-center gap-3">
          {meta ? <div className={SURFACE_PAGE_META}>{meta}</div> : null}
          {actions}
        </div>
      )}
    </div>
  );
}

export type SurfacePageColumnsProps = HTMLAttributes<HTMLDivElement> & {
  ratio?: 'golden' | 'equal';
};

export function SurfacePageColumns({
  children,
  className,
  ratio = 'golden',
  ...rest
}: SurfacePageColumnsProps) {
  return (
    <div
      className={cn(
        ratio === 'golden'
          ? 'grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.618fr)_minmax(0,1fr)]'
          : 'grid grid-cols-1 gap-8 lg:grid-cols-2',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

const SurfacePageWidthContext = createContext<SurfacePageWidth>('content');

function useSurfacePageWidth(): SurfacePageWidth {
  return useContext(SurfacePageWidthContext);
}

/** Scroll root for Interior operational surfaces (settings, admin). */
export function SurfacePageLayout({
  children,
  width = 'content',
}: {
  children: ReactNode;
  width?: SurfacePageWidth;
}) {
  return (
    <SurfacePageWidthContext.Provider value={width}>
      <div
        className={cn(
          'h-full min-h-0 overflow-x-hidden overflow-y-auto',
          APP_CHROME_CANVAS,
        )}
      >
        {children}
      </div>
    </SurfacePageWidthContext.Provider>
  );
}

export function SurfacePageBody({
  children,
  width,
  className,
  as: Tag = 'main',
  padTop = true,
}: {
  children: ReactNode;
  width?: SurfacePageWidth;
  className?: string;
  as?: 'main' | 'div';
  padTop?: boolean;
}) {
  const layoutWidth = useSurfacePageWidth();
  const resolvedWidth = width ?? layoutWidth;

  return (
    <Tag
      className={cn(
        surfacePageWidthClassName(resolvedWidth),
        'pb-8',
        padTop && SURFACE_PAGE_MOBILE_TOP_PAD,
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export type SurfacePageCardProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  as?: 'div' | 'section' | 'article';
};

/** Large rounded content container for Interior catalog/grid pages. */
export function SurfacePageCard({
  children,
  className,
  as: Tag = 'div',
  ...rest
}: SurfacePageCardProps) {
  return (
    <Tag className={cn(SURFACE_PAGE_CARD, className)} {...rest}>
      {children}
    </Tag>
  );
}

export type SurfacePagePanelProps = SurfacePageSectionHeaderProps & {
  children: ReactNode;
  /** Extra classes on the body region (the padded area under the header). */
  contentClassName?: string;
  /** Cap the body height; the body scrolls internally, the header stays. */
  maxHeight?: string;
  /** Edge-to-edge body — no padding (full-bleed tables / charts). */
  flush?: boolean;
  as?: 'section' | 'div' | 'article';
};

/**
 * Modular dashboard panel — the working-surface card grammar (prior-art
 * register, docs/audits/random_examples): every region sits on a matte card
 * (`card-matte-2`: --surface-2 face + inset hairline ring) with its title
 * INSIDE the card, meta/actions right, hairline under the header, padded body.
 * Content panels use this; glass stays for floating chrome (drawers,
 * dropdowns, overlays). Replaces the naked
 * `<section><SurfacePageSectionHeader/>…</section>` idiom on module scenes.
 */
export function SurfacePagePanel({
  title,
  subtitle,
  meta,
  actions,
  className,
  contentClassName,
  maxHeight,
  flush = false,
  as: Tag = 'section',
  children,
}: SurfacePagePanelProps) {
  return (
    <Tag className={cn('card-matte-2 flex min-w-0 flex-col', className)}>
      <div className="hairline-b px-4 pb-2.5 pt-3">
        <SurfacePageSectionHeader
          title={title}
          subtitle={subtitle}
          meta={meta}
          actions={actions}
        />
      </div>
      {maxHeight ? (
        <ScrollCue
          className={cn('min-h-0 flex-1', flush ? 'pb-1' : 'px-4 py-3', contentClassName)}
          maxHeight={maxHeight}
        >
          {children}
        </ScrollCue>
      ) : (
        <div className={cn('min-h-0 flex-1', flush ? 'pb-1' : 'px-4 py-3', contentClassName)}>
          {children}
        </div>
      )}
    </Tag>
  );
}
